"use client";

import { useState, type FormEvent } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { PhoneInput, type Value, type Country } from "@/components/reui/phone-input";
import type { OtpMethod } from "@/types";

interface PhoneStepProps {
  onSubmit: (phone: string, method?: OtpMethod) => void;
  isPending: boolean;
  error: Error | null;
}

export function PhoneStep({ onSubmit, isPending, error }: PhoneStepProps) {
  const [value, setValue] = useState<Value | undefined>(undefined);
  const [country, setCountry] = useState<Country | undefined>("DZ" as Country);
  const [method, setMethod] = useState<OtpMethod>("whatsapp");

  const isAlgeria = country === "DZ";

  const isValid = !!value && isValidPhoneNumber(value);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || !value) return;
    onSubmit(value, isAlgeria ? method : "whatsapp");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-headline mb-1.5">
          Numéro de téléphone
        </label>
        <PhoneInput
          value={value}
          onChange={setValue}
          onCountryChange={setCountry}
          defaultCountry={"DZ" as Country}
          placeholder="Entrez votre numéro"
          autoFocus
        />
      </div>

      {/* Channel choice only appears once a valid number is entered. */}
      {isValid && (
      <div>
        <label className="block text-sm font-semibold text-headline mb-1.5">
          Recevoir le code via
        </label>
        {isAlgeria ? (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setMethod("whatsapp")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                method === "whatsapp"
                  ? "border-primary bg-primary/10 text-secondary dark:text-primary"
                  : "border-gray-300 dark:border-[#313131] text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              WhatsApp
            </button>
            <button
              type="button"
              onClick={() => setMethod("sms")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                method === "sms"
                  ? "border-primary bg-primary/10 text-secondary dark:text-primary"
                  : "border-gray-300 dark:border-[#313131] text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              SMS
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-2 rounded-lg border border-primary bg-primary/10 px-3 py-2.5 text-sm font-medium text-secondary dark:text-primary">
            WhatsApp
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
              Seul canal disponible hors Algérie
            </span>
          </div>
        )}
      </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <button
        type="submit"
        disabled={!isValid || isPending}
        className="w-full rounded-full bg-secondary py-3 text-sm font-bold text-white transition hover:bg-primary/90 hover:text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isPending ? "Envoi du code..." : "Continuer"}
      </button>
    </form>
  );
}
